/**
 * Animation update hook for real-time puppet animation (Parametric System)
 *
 * This hook uses React Three Fiber's useFrame to update bone transforms
 * based on the current timeline playhead position and active tools.
 *
 * Parametric Animation System Features:
 * - Absolute target rotations (no more relative intensity-based movement)
 * - Easing functions: linear, ease-in, ease-out, ease-in-out
 * - Animation state tracking: maintains current rotation per body part
 * - Hold-last-pose: maintains rotation during gaps (no snap to T-pose)
 * - Last-wins overlap: later-starting tool takes full control (no blending)
 * - Motor limit clamping: respects min/max angle limits from puppet config
 * - Initialize to startingPosition on scene load/reset
 * - Position control actions (Reset to Start, Dock Puppet) via pendingPositionAction
 */

import { useFrame } from '@react-three/fiber';
import { useRef, useEffect, useCallback } from 'react';
import {
  useTimelineStore,
  getGroupDuration,
} from '../../../stores/timelineStore';
import { usePuppetStore } from '../../../stores/puppetStore';
import type { Tool, PlacedGroup, Group } from '../../../stores/timelineStore';
import type { PuppetModelRef } from '../PuppetModel';
import type { BodyPart } from '../utils/boneMapping';
import type {
  MotorBodyPart,
  MotorPositions,
} from '../../../stores/puppetStore';
import { getEasedProgress, interpolateRotation } from '../../../utils/easing';
import {
  applyPositions as applyPositionsUtil,
  MOTOR_BODY_PARTS,
} from '../../../utils/animation';
import {
  isResetPositionTool,
  getResetTargetPositions,
} from '../../../utils/resetPositionTool';
import type { ResetTargetPosition } from '../../../stores/timelineStore';

/**
 * Animation state that tracks current rotation for each body part.
 * This allows hold-last-pose behavior during gaps.
 */
export interface AnimationState {
  currentRotations: MotorPositions;
  lastToolEndTimes: Record<MotorBodyPart, number>;
  /** Tracks the "from" rotation when each tool started animating */
  activeToolFromRotations: Record<MotorBodyPart, number>;
  /** Tracks which tool ID is currently animating each body part */
  activeToolIds: Record<MotorBodyPart, string | null>;
  isInitialized: boolean;
}

/**
 * Create initial animation state from puppet starting position
 */
export function createInitialAnimationState(
  startingPosition?: MotorPositions
): AnimationState {
  const defaultPositions: MotorPositions = {
    head: 0,
    body: 0,
    leftArm: 0,
    rightArm: 0,
    leftLeg: 0,
    rightLeg: 0,
  };

  return {
    // Spread to create a new mutable object (Zustand store objects are frozen)
    currentRotations: startingPosition
      ? { ...startingPosition }
      : defaultPositions,
    lastToolEndTimes: {
      head: -Infinity,
      body: -Infinity,
      leftArm: -Infinity,
      rightArm: -Infinity,
      leftLeg: -Infinity,
      rightLeg: -Infinity,
    },
    activeToolFromRotations: {
      head: 0,
      body: 0,
      leftArm: 0,
      rightArm: 0,
      leftLeg: 0,
      rightLeg: 0,
    },
    activeToolIds: {
      head: null,
      body: null,
      leftArm: null,
      rightArm: null,
      leftLeg: null,
      rightLeg: null,
    },
    isInitialized: false,
  };
}

/**
 * Calculate which tools are active at the given playhead position
 *
 * A tool is active if the playhead is within its time range:
 * playhead >= startTime && playhead < endTime
 * (Note: exclusive end to prevent double-counting at tool boundaries)
 *
 * Also includes tools from active PlacedGroups (Group Sub-Timeline feature):
 * - For each PlacedGroup active at playhead, calculate relative time
 * - Include internal GroupTools that are active at the relative time
 * - Convert GroupTools to Tool-like objects with absolute timing
 */
export function getActiveTools(
  tools: Tool[],
  playheadPosition: number,
  placedGroups?: PlacedGroup[],
  groups?: Group[]
): Tool[] {
  // Start with directly placed tools
  const activeTools = tools.filter((tool) => {
    const endTime = tool.startTime + tool.duration;
    return playheadPosition >= tool.startTime && playheadPosition <= endTime;
  });

  // If no placed groups provided, return just the direct tools
  if (!placedGroups || !groups || placedGroups.length === 0) {
    return activeTools;
  }

  // Process placed groups to find active internal tools
  for (const placedGroup of placedGroups) {
    // Find the group definition
    const group = groups.find((g) => g.id === placedGroup.groupId);
    if (!group || group.tools.length === 0) continue;

    // Calculate group's time span
    const groupDuration = getGroupDuration(group);
    const groupEndTime = placedGroup.startTime + groupDuration;

    // Check if playhead is within this placed group's span
    if (
      playheadPosition < placedGroup.startTime ||
      playheadPosition > groupEndTime
    ) {
      continue;
    }

    // Calculate relative time within the group
    const relativeTime = playheadPosition - placedGroup.startTime;

    // Find active internal tools at the relative time
    for (let i = 0; i < group.tools.length; i++) {
      const groupTool = group.tools[i];
      const toolEndTime = groupTool.relativeStartTime + groupTool.duration;

      // Check if this internal tool is active at the relative time
      if (
        relativeTime >= groupTool.relativeStartTime &&
        relativeTime <= toolEndTime
      ) {
        // Convert GroupTool to Tool-like object with absolute timing
        const absoluteTool: Tool = {
          // Generate unique ID: placedGroup.id + group.id + tool index
          id: `${placedGroup.id}-${group.id}-tool-${i}`,
          toolId: groupTool.toolId,
          // Convert relative to absolute timing
          trackNumber: groupTool.relativeTrackNumber,
          startTime: placedGroup.startTime + groupTool.relativeStartTime,
          duration: groupTool.duration,
          bodyPart: groupTool.bodyPart,
          targetRotation: groupTool.targetRotation,
          easing: groupTool.easing,
          targetPosition: groupTool.targetPosition,
          puppetId: groupTool.puppetId ?? placedGroup.puppetId,
        };

        activeTools.push(absoluteTool);
      }
    }
  }

  return activeTools;
}

/**
 * Group tools by body part
 *
 * Returns a map of body parts to arrays of tools affecting that body part.
 * Excludes 'control' body parts (wait, stop tools).
 */
export function groupToolsByBodyPart(tools: Tool[]): Record<string, Tool[]> {
  const grouped: Record<string, Tool[]> = {};

  for (const tool of tools) {
    // Skip control tools - they don't affect body part rotations
    if (tool.bodyPart === 'control') continue;

    if (!grouped[tool.bodyPart]) {
      grouped[tool.bodyPart] = [];
    }
    grouped[tool.bodyPart].push(tool);
  }

  return grouped;
}

/**
 * Find the winning tool using "last-wins" behavior
 *
 * When multiple tools affect the same body part at the same time:
 * - The tool with the later startTime takes full control
 * - If equal startTime, the tool that appears later in the array wins
 * - NO blending between overlapping tools
 *
 * @param tools - Array of tools affecting the same body part
 * @returns The winning tool, or null if no tools provided
 */
export function findWinningTool(tools: Tool[]): Tool | null {
  if (tools.length === 0) return null;
  if (tools.length === 1) return tools[0];

  // Sort by startTime descending (later start = higher priority)
  // When equal, the later tool in the original array wins (stable sort)
  return [...tools].sort((a, b) => {
    if (b.startTime !== a.startTime) {
      return b.startTime - a.startTime;
    }
    // If equal start times, later in original array wins
    // We rely on the fact that the tools are already in their natural order
    return 0;
  })[0];
}

/**
 * Calculate the rotation for a body part at the current playhead position
 *
 * Implements parametric interpolation:
 * 1. Calculate progress: (currentTime - tool.startTime) / tool.duration
 * 2. Apply easing function to progress
 * 3. Interpolate FROM previous position TO targetRotation
 * 4. Clamp result to motor min/max limits
 *
 * @param tool - The active tool
 * @param playheadPosition - Current playhead time in seconds
 * @param previousRotation - Rotation value before this tool started
 * @param clampFn - Function to clamp rotation to motor limits
 * @returns Interpolated and clamped rotation in degrees
 */
export function calculateParametricRotation(
  tool: Tool,
  playheadPosition: number,
  previousRotation: number,
  clampFn: (bodyPart: string, angle: number) => number
): number {
  // Calculate progress through the tool (0 to 1)
  const rawProgress = (playheadPosition - tool.startTime) / tool.duration;
  const progress = Math.max(0, Math.min(1, rawProgress));

  // Apply easing function to get curved progress
  const easedProgress = getEasedProgress(progress, tool.easing);

  // Interpolate from previous position to target rotation
  const interpolatedRotation = interpolateRotation(
    previousRotation,
    tool.targetRotation,
    easedProgress
  );

  // Clamp to motor limits
  return clampFn(tool.bodyPart, interpolatedRotation);
}

/**
 * Convert degrees to radians for Three.js bone rotation
 */
function degreesToRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

/**
 * Motor config result with axis, bone offset, direction, and static offsets
 */
interface MotorConfigForAnimation {
  axis: 'x' | 'y' | 'z';
  boneOffset: number;
  direction: 1 | -1;
  xOffset: number;
  yOffset: number;
  zOffset: number;
}

/**
 * Get the motor config (axis, boneOffset, direction, xOffset, yOffset, zOffset) for a body part from puppet config
 * Returns default values if not configured
 */
function getMotorConfigForAnimation(
  bodyPart: MotorBodyPart
): MotorConfigForAnimation {
  const puppetState = usePuppetStore.getState();
  const motorConfig = puppetState.getMotorConfig(bodyPart);
  return {
    axis: motorConfig?.axis ?? 'x',
    boneOffset: motorConfig?.boneOffset ?? 0,
    direction: motorConfig?.direction ?? 1,
    xOffset: motorConfig?.xOffset ?? 0,
    yOffset: motorConfig?.yOffset ?? 0,
    zOffset: motorConfig?.zOffset ?? 0,
  };
}

/**
 * Apply rotation to a bone on the appropriate axis
 *
 * Converts motor degrees to bone rotation using boneOffset to compensate
 * for GLTF model rest pose (e.g., T-pose arms).
 *
 * Formula: boneRotation = (motorDegrees * direction) + boneOffset
 */
function applyRotationToBone(
  puppetRef: PuppetModelRef,
  bodyPart: BodyPart,
  motorDegrees: number,
  axis: 'x' | 'y' | 'z',
  boneOffset: number = 0,
  direction: 1 | -1 = 1,
  xOffset: number = 0,
  yOffset: number = 0,
  zOffset: number = 0
): void {
  const bone = puppetRef.getBoneByBodyPart(bodyPart);
  if (!bone) return;

  // Convert motor degrees to bone rotation (apply direction and offset for rest pose)
  // Formula: boneRotation = (motorDegrees * direction) + boneOffset
  const boneRotationDegrees = motorDegrees * direction + boneOffset;
  const radians = degreesToRadians(boneRotationDegrees);

  // Apply controlled axis rotation + static offsets on other axes
  if (axis === 'x') {
    bone.rotation.x = radians;
    if (yOffset !== 0) bone.rotation.y = degreesToRadians(yOffset);
    if (zOffset !== 0) bone.rotation.z = degreesToRadians(zOffset);
  } else if (axis === 'y') {
    bone.rotation.y = radians;
    if (xOffset !== 0) bone.rotation.x = degreesToRadians(xOffset);
    if (zOffset !== 0) bone.rotation.z = degreesToRadians(zOffset);
  } else {
    bone.rotation.z = radians;
    if (xOffset !== 0) bone.rotation.x = degreesToRadians(xOffset);
    if (yOffset !== 0) bone.rotation.y = degreesToRadians(yOffset);
  }
}

/**
 * Return type for useAnimationUpdate hook
 */
export interface AnimationUpdateResult {
  /**
   * Apply positions instantly to all bones (no animation)
   * Used for Reset to Start and Dock Puppet operations
   */
  applyPositions: (positions: MotorPositions) => boolean;

  /**
   * Get the current animation state
   */
  getAnimationState: () => AnimationState;

  /**
   * Reset animation state to the given positions
   */
  resetAnimationState: (positions: MotorPositions) => void;
}

/**
 * Animation update hook with parametric interpolation
 *
 * Features:
 * - Tracks current rotation per body part (animation state)
 * - Initializes to startingPosition from puppet config
 * - Uses parametric interpolation (easing + lerp to target rotation)
 * - Implements hold-last-pose during gaps
 * - Implements last-wins for overlapping tools
 * - Clamps to motor limits
 * - Exports applyPositions function for control panel use
 * - Processes pendingPositionAction for Reset to Start and Dock Puppet buttons
 *
 * @param puppetRef - Reference to PuppetModel component
 * @returns Animation control functions for external use
 */
export function useAnimationUpdate(
  puppetRef: React.RefObject<PuppetModelRef>
): AnimationUpdateResult {
  // Animation state ref - persists across frames
  const animationStateRef = useRef<AnimationState>(
    createInitialAnimationState()
  );

  // Track previous playhead to detect direction changes and resets
  const prevPlayheadRef = useRef<number>(0);

  // Track if we've initialized from puppet config
  const hasInitializedRef = useRef<boolean>(false);

  // Track if we've applied initial positions to the 3D model
  const hasAppliedInitialPositionsRef = useRef<boolean>(false);

  // Initialize animation state when puppet loads or changes
  useEffect(() => {
    const puppetState = usePuppetStore.getState();
    const activePuppet = puppetState.activePuppet;

    if (activePuppet) {
      animationStateRef.current = createInitialAnimationState(
        activePuppet.startingPosition
      );
      animationStateRef.current.isInitialized = true;
      hasInitializedRef.current = true;
    }
  }, []);

  // Subscribe to puppet store changes to reinitialize on puppet change
  useEffect(() => {
    const unsubscribe = usePuppetStore.subscribe((state) => {
      if (state.activePuppet && !hasInitializedRef.current) {
        animationStateRef.current = createInitialAnimationState(
          state.activePuppet.startingPosition
        );
        animationStateRef.current.isInitialized = true;
        hasInitializedRef.current = true;
      }
    });

    return () => unsubscribe();
  }, []);

  // Apply initial positions to the 3D model when skeleton becomes available
  // This ensures the puppet starts in its starting position (e.g., arms down)
  // instead of the GLTF's default T-pose
  useEffect(() => {
    // Check periodically until skeleton is available and positions are applied
    const checkAndApply = () => {
      if (hasAppliedInitialPositionsRef.current) return;

      const puppetState = usePuppetStore.getState();
      const activePuppet = puppetState.activePuppet;

      if (puppetRef.current?.skeleton && activePuppet?.startingPosition) {
        // Apply starting positions to 3D model bones (with boneOffset, direction, and static offsets for rest pose)
        applyPositionsUtil(
          puppetRef.current,
          activePuppet.startingPosition,
          (bodyPart: MotorBodyPart) => {
            const motorConfig = puppetState.getMotorConfig(bodyPart);
            return {
              axis: motorConfig?.axis ?? 'x',
              boneOffset: motorConfig?.boneOffset ?? 0,
              direction: motorConfig?.direction ?? 1,
              xOffset: motorConfig?.xOffset ?? 0,
              yOffset: motorConfig?.yOffset ?? 0,
              zOffset: motorConfig?.zOffset ?? 0,
            };
          }
        );
        hasAppliedInitialPositionsRef.current = true;
      }
    };

    // Initial check
    checkAndApply();

    // Also check on a short interval to catch when skeleton loads
    const interval = setInterval(checkAndApply, 100);

    // Clean up after 3 seconds (skeleton should definitely be loaded by then)
    const timeout = setTimeout(() => {
      clearInterval(interval);
    }, 3000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [puppetRef]);

  /**
   * Apply positions instantly to all bones (no animation)
   * Also updates the animation state to reflect the new positions
   */
  const applyPositions = useCallback(
    (positions: MotorPositions): boolean => {
      if (!puppetRef.current?.skeleton) {
        return false;
      }

      // Apply to 3D model bones (with boneOffset for rest pose compensation)
      const success = applyPositionsUtil(
        puppetRef.current,
        positions,
        getMotorConfigForAnimation
      );

      // Update animation state to match new positions
      if (success) {
        animationStateRef.current.currentRotations = { ...positions };
      }

      return success;
    },
    [puppetRef]
  );

  /**
   * Get the current animation state
   *
   * Returns a shallow copy to ensure React state updates trigger re-renders.
   * (The ref object is mutated each frame, so we need a new reference for React)
   */
  const getAnimationState = useCallback((): AnimationState => {
    const state = animationStateRef.current;
    return {
      currentRotations: { ...state.currentRotations },
      lastToolEndTimes: { ...state.lastToolEndTimes },
      activeToolFromRotations: { ...state.activeToolFromRotations },
      activeToolIds: { ...state.activeToolIds },
      isInitialized: state.isInitialized,
    };
  }, []);

  /**
   * Reset animation state to the given positions
   */
  const resetAnimationState = useCallback((positions: MotorPositions): void => {
    animationStateRef.current = createInitialAnimationState(positions);
    animationStateRef.current.isInitialized = true;
  }, []);

  useFrame(() => {
    // Get current state from stores using getState() to avoid re-renders
    const { playheadPosition, tools, isPlaying, placedGroups, groups } =
      useTimelineStore.getState();
    const {
      clampRotation,
      activePuppet,
      pendingPositionAction,
      clearPendingPositionAction,
    } = usePuppetStore.getState();

    // Process pending position action (Task Group 4.5)
    // This handles Reset to Start and Dock Puppet button clicks
    if (pendingPositionAction && activePuppet && puppetRef.current?.skeleton) {
      const positions =
        pendingPositionAction === 'reset'
          ? activePuppet.startingPosition
          : activePuppet.dockingPosition;

      // Apply positions to 3D model
      applyPositions(positions);

      // Clear the pending action so it doesn't repeat
      clearPendingPositionAction();

      // Don't process normal animation this frame
      return;
    }

    // Detect playhead changes (for scrubbing support)
    const playheadChanged = playheadPosition !== prevPlayheadRef.current;
    const prevPlayhead = prevPlayheadRef.current;
    prevPlayheadRef.current = playheadPosition;

    // Detect playhead reset (jumped backwards significantly)
    const playheadReset = playheadPosition < prevPlayhead - 0.5;

    // Only update when playing, scrubbing, or reset
    if (!isPlaying && !playheadChanged) return;

    // Safety check: ensure puppet ref and skeleton are available
    if (!puppetRef.current?.skeleton) return;

    // On playhead reset to near 0, reinitialize to starting position
    if (playheadReset && playheadPosition < 0.1 && activePuppet) {
      animationStateRef.current = createInitialAnimationState(
        activePuppet.startingPosition
      );
    }

    // Get animation state
    const animState = animationStateRef.current;

    // Find tools active at current playhead (includes tools from placed groups)
    const activeTools = getActiveTools(
      tools,
      playheadPosition,
      placedGroups,
      groups
    );

    // Check for active Reset Position tool (affects ALL body parts)
    const resetPositionTool = activeTools.find((tool) =>
      isResetPositionTool(tool.toolId)
    );

    if (resetPositionTool && activePuppet) {
      // Get target positions based on tool's targetPosition parameter
      const targetPosition: ResetTargetPosition =
        resetPositionTool.targetPosition ?? 'start';
      const targetPositions = getResetTargetPositions(
        targetPosition,
        activePuppet.startingPosition,
        activePuppet.dockingPosition
      );

      // Track if this is a new Reset Position tool (for "from" capture)
      const resetToolId = `reset-${resetPositionTool.id}`;
      const isNewResetTool = animState.activeToolIds.head !== resetToolId;

      // Process all body parts simultaneously
      for (const bodyPart of MOTOR_BODY_PARTS) {
        if (isNewResetTool) {
          // Capture "from" rotation when Reset Position tool starts
          animState.activeToolFromRotations[bodyPart] =
            animState.currentRotations[bodyPart];
          animState.activeToolIds[bodyPart] = resetToolId;
        }

        // Calculate progress through the tool
        const rawProgress =
          (playheadPosition - resetPositionTool.startTime) /
          resetPositionTool.duration;
        const progress = Math.max(0, Math.min(1, rawProgress));

        // Apply easing
        const easedProgress = getEasedProgress(
          progress,
          resetPositionTool.easing
        );

        // Interpolate from current to target
        const fromRotation = animState.activeToolFromRotations[bodyPart];
        const targetRotation = targetPositions[bodyPart];
        const newRotation = interpolateRotation(
          fromRotation,
          targetRotation,
          easedProgress
        );

        // Clamp to motor limits
        const clampedRotation = clampRotation(bodyPart, newRotation);

        // Update animation state
        animState.currentRotations[bodyPart] = clampedRotation;

        // Check if tool has completed
        const toolEndTime =
          resetPositionTool.startTime + resetPositionTool.duration;
        const toolCompleted = playheadPosition >= toolEndTime;

        if (toolCompleted) {
          // Commit final clamped target position
          animState.currentRotations[bodyPart] = clampRotation(
            bodyPart,
            targetRotation
          );
          animState.lastToolEndTimes[bodyPart] = toolEndTime;
          animState.activeToolIds[bodyPart] = null;
        }

        // Apply rotation to bone (with boneOffset, direction, and static offsets for rest pose compensation)
        const config = getMotorConfigForAnimation(bodyPart);
        applyRotationToBone(
          puppetRef.current!,
          bodyPart,
          clampedRotation,
          config.axis,
          config.boneOffset,
          config.direction,
          config.xOffset,
          config.yOffset,
          config.zOffset
        );
      }

      // Skip normal body part processing when Reset Position is active
      return;
    }

    // Group active tools by body part
    const toolsByBodyPart = groupToolsByBodyPart(activeTools);

    // Process each body part
    for (const bodyPart of MOTOR_BODY_PARTS) {
      const bodyPartTools = toolsByBodyPart[bodyPart];

      if (bodyPartTools && bodyPartTools.length > 0) {
        // LAST-WINS: Find the winning tool (later startTime wins)
        const winningTool = findWinningTool(bodyPartTools);

        if (winningTool) {
          // Check if this is a new tool starting (different from last active tool)
          const isNewTool =
            animState.activeToolIds[bodyPart] !== winningTool.id;

          if (isNewTool) {
            // Capture the "from" rotation when a new tool starts
            // This is the current position before the tool begins animating
            animState.activeToolFromRotations[bodyPart] =
              animState.currentRotations[bodyPart];
            animState.activeToolIds[bodyPart] = winningTool.id;
          }

          // Use the captured "from" rotation for interpolation
          const fromRotation = animState.activeToolFromRotations[bodyPart];

          // Calculate parametric rotation with easing
          const newRotation = calculateParametricRotation(
            winningTool,
            playheadPosition,
            fromRotation,
            clampRotation
          );

          // Check if tool has completed (at or past end time)
          const toolEndTime = winningTool.startTime + winningTool.duration;
          const toolCompleted = playheadPosition >= toolEndTime;

          // Update animation state with live interpolated position
          // This allows the debug panel to show the actual current position
          animState.currentRotations[bodyPart] = newRotation;

          // When tool completes, commit the target rotation (clamped) and clear active tool
          if (toolCompleted) {
            animState.currentRotations[bodyPart] = clampRotation(
              bodyPart,
              winningTool.targetRotation
            );
            animState.lastToolEndTimes[bodyPart] = toolEndTime;
            animState.activeToolIds[bodyPart] = null;
          }

          // Apply rotation to bone (with boneOffset, direction, and static offsets for rest pose compensation)
          const config = getMotorConfigForAnimation(bodyPart);
          applyRotationToBone(
            puppetRef.current!,
            bodyPart,
            newRotation,
            config.axis,
            config.boneOffset,
            config.direction,
            config.xOffset,
            config.yOffset,
            config.zOffset
          );
        }
      } else {
        // HOLD-LAST-POSE: No active tool for this body part
        // Clear the active tool tracking for this body part
        animState.activeToolIds[bodyPart] = null;

        // Apply the current rotation state (maintains last position)
        // Include boneOffset, direction, and static offsets to maintain correct visual position
        const currentRotation = animState.currentRotations[bodyPart];
        const config = getMotorConfigForAnimation(bodyPart);
        applyRotationToBone(
          puppetRef.current!,
          bodyPart,
          currentRotation,
          config.axis,
          config.boneOffset,
          config.direction,
          config.xOffset,
          config.yOffset,
          config.zOffset
        );
      }
    }
  });

  return {
    applyPositions,
    getAnimationState,
    resetAnimationState,
  };
}

// Legacy exports for backwards compatibility with existing tests
// These can be removed once tests are updated

/**
 * @deprecated Use getEasedProgress from utils/easing.ts instead
 */
export function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

/**
 * @deprecated No longer used in parametric system
 */
export function calculateOverlapPercentage(
  tool: Tool,
  playheadPosition: number
): number {
  const progress = (playheadPosition - tool.startTime) / tool.duration;
  const clampedProgress = Math.max(0, Math.min(1, progress));
  return easeInOutCubic(clampedProgress);
}

/**
 * @deprecated No longer used in parametric system
 */
export function calculateToolRotation(
  _toolDef: unknown,
  _progress: number,
  _speed: number,
  _intensity: number
): { x: number; y: number; z: number } {
  console.warn(
    'calculateToolRotation is deprecated. Use calculateParametricRotation instead.'
  );
  return { x: 0, y: 0, z: 0 };
}

/**
 * @deprecated No longer used - parametric system uses last-wins, not blending
 */
export function applyToolTransforms(
  _puppetRef: PuppetModelRef,
  _bodyPart: BodyPart,
  _tools: Tool[],
  _playheadPosition: number
): void {
  console.warn(
    'applyToolTransforms is deprecated. Parametric system uses last-wins, not blending.'
  );
}
